import { Col } from 'antd';
import { FieldValidator, FORM_ERROR, SubmissionErrors } from 'final-form';
import { omit } from 'lodash';
import { FC } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { routes } from '~src/routingConfig';
import { defaultBaseApi } from '~src/services/ApiController';
import { validatorMinValue6, validatorRequired, validatorTrimmed } from '~src/services/validators';
import BigSuccess from '~src/shared/BigSuccess';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import {
  ActionRow,
  ButtonSection,
  Footer,
  FormErrors,
  SectionBody,
  SectionHeader,
  SubSection,
} from '~src/shared/form/shared.sc';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { TileContainer } from '~src/styles/containers.sc';
import { FieldType } from '~src/types';
import { trimValues } from '~src/utils';

interface UseOtpFormValues {
  identifier: string;
  otp: string;
  password: string;
  passwordConfirm: string;
}

const UseOtpForm: FC = () => {
  const validatePasswordConfirmation: FieldValidator<string> = (confirmValue: string, allValues?: object) =>
    allValues && (allValues as { password: string }).password !== confirmValue
      ? 'password_confirmation_invalid'
      : undefined;

  const renderInput = ({ autoFocus, input, labelKey, meta: { error, touched } }: FieldType) => {
    return (
      <CustomFormInput
        {...input}
        autoFocus={autoFocus}
        type={input.type}
        error={touched && error ? error : null}
        label={t(labelKey)}
        labelKey={labelKey}
      />
    );
  };

  const { t } = useTranslation();

  const onSubmit: (formValues: UseOtpFormValues) => Promise<SubmissionErrors | void> = (
    formValues: UseOtpFormValues,
  ) => {
    return defaultBaseApi
      .passwordSetFromOtp(trimValues(omit(formValues, 'passwordConfirm')))
      .then(_ => {
        return;
      })
      .catch(axiosError => {
        let errorMessage = 'unknown error';
        if (axiosError.response.data && axiosError.response.data.error) {
          errorMessage = t(`x.error.${axiosError.response.data.error}`);
        }
        return {
          [FORM_ERROR]: errorMessage,
        };
      });
  };
  return (
    <>
      <Form onSubmit={onSubmit}>
        {({ form, handleSubmit, pristine, submitSucceeded, submitting, submitError }) => {
          const reset = () => form.reset();

          return submitSucceeded ? (
            <TileContainer>
              <SectionHeader>{t('x.otp.success')}</SectionHeader>
              <SectionBody>
                <BigSuccess text={t('x.otp.link-login')} linkTo={routes.login} />
              </SectionBody>
            </TileContainer>
          ) : (
            <TileContainer>
              <form onSubmit={handleSubmit} autoComplete="off">
                <SectionHeader data-translation="x.otp.form.identifier">
                  {t('x.otp.form.identifier')}
                </SectionHeader>
                <SectionBody>
                    <Field
                      labelKey="x.otp.form.identifier"
                      name="identifier"
                      render={renderInput}
                      type="text"
                      validate={validatorRequired}
                    />
                    <Field
                      labelKey="x.otp.form.otp"
                      name="otp"
                      render={renderInput}
                      type="password"
                      validate={validatorRequired}
                    />
                </SectionBody>
                <SectionHeader data-translation="x.otp.form.identifier">
                  {t('x.otp.form.password')}
                </SectionHeader>
                <SectionBody>
                    <Field
                      labelKey="x.otp.form.password"
                      name="password"
                      render={renderInput}
                      type="password"
                      validate={composeValidators([
                        validatorRequired,
                        validatorMinValue6,
                        validatorTrimmed,
                      ])}
                    />
                    <Field
                      labelKey="x.otp.form.passwordConfirm"
                      name="passwordConfirm"
                      render={renderInput}
                      type="password"
                      validate={validatePasswordConfirmation}
                      validateFields={['password']}
                    />
                </SectionBody>
                {submitError && <FormErrors>{submitError}</FormErrors>}
                {!pristine && (
                  <Footer>
                    <ActionRow>
                      <ButtonSection>
                        <SecondaryButton onClick={reset} type="reset">
                          <span data-translation="x.button.reset">{t('x.button.reset')}</span>
                        </SecondaryButton>

                        <PrimaryButtonLoading isLoading={submitting}>
                          <span data-translation="x.button.save">{t('x.button.save')}</span>
                        </PrimaryButtonLoading>
                      </ButtonSection>
                    </ActionRow>
                  </Footer>
                )}
              </form>
            </TileContainer>
          );
        }}
      </Form>
    </>
  );
};

export default UseOtpForm;
